import { useMemo } from 'react';
import { ApolloClient, useApolloClient } from '@apollo/client';
import {
  PREPARE_WAITINGLIST_APPROVAL_UPLOAD,
  DELETE_WAITINGLIST_APPROVAL_ATTACHMENT,
} from 'graphql/mutations/uploadWaitingListAttachment';
import { GET_WAITINGLIST_ATTACHMENT_DOWNLOAD_URL } from 'graphql/queries/getWaitingListAttachmentDownloadUrl';
import { normalizeMimeType } from 'utils/attachmentUtils';
import type { AttachmentFile } from 'types/attachmentTypes';
import type { AttachmentFlow } from './types';

export const createWaitingListAttachmentFlow = (
  client: ApolloClient<unknown>
): AttachmentFlow => ({
  prepareUpload: async (file, contextId) => {
    const res = await client.mutate({
      mutation: PREPARE_WAITINGLIST_APPROVAL_UPLOAD,
      variables: {
        pmcId: contextId,
        mimeType: normalizeMimeType(file.type),
      },
    });
    const meta = res.data?.prepareWaitingListApprovalUpload;
    if (!meta?.attachmentUid || !meta?.uploadUrl) {
      throw new Error('Prepare upload response is incomplete');
    }
    return { attachmentUid: meta.attachmentUid, uploadUrl: meta.uploadUrl };
  },

  fetchDownloadUrl: async ({ uploadUid, entityId, entitySource }) => {
    const { data } = await client.query({
      query: GET_WAITINGLIST_ATTACHMENT_DOWNLOAD_URL,
      variables: {
        id: entityId,
        attachmentUid: uploadUid,
        source: entitySource,
      },
      fetchPolicy: 'network-only',
    });
    const result = data?.waitingListAttachmentDownloadUrl;
    if (!result) return null;
    return { preSignedUrl: result.preSignedUrl };
  },

  deleteAttachment: async (file: AttachmentFile) => {
    if (!file.uploadUid) return true;
    const res = await client.mutate({
      mutation: DELETE_WAITINGLIST_APPROVAL_ATTACHMENT,
      variables: { attachmentUid: file.uploadUid },
    });
    return !!res.data?.deleteWaitingListApprovalAttachment;
  },
});

export const useWaitingListAttachmentFlow = (): AttachmentFlow => {
  const client = useApolloClient();
  return useMemo(() => createWaitingListAttachmentFlow(client), [client]);
};
